import { useState } from 'react';
import { Loader2, Star, ThumbsUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import tinycolor from 'tinycolor2';
import SurveyChat from './SurveyChat';
import surveysService from '../../services/surveysService';
import { getTranslation } from '../../lib/translations';

const EMOJIS = ['😠', '🙁', '😐', '🙂', '😍'];

// The nps and feedback modes: one score question, an optional comment, then either the thank you card or,
// when the survey asks the AI to follow up, the chat continuing from the score.
function FixedSurvey({
  survey, preview = false, page, inviteId,
}) {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const design = survey.design || {};
  const fixed = survey.fixed || {};
  const languageCode = survey.settings?.language || 'en';
  const buttonColor = design.sendButtonColor || '#6366f1';
  const buttonText = readableOn(buttonColor);

  const submit = async (event) => {
    event.preventDefault();
    if(score === null || sending) {
      return;
    }
    if(preview) {
      setResponse({ isComplete: true });
      return;
    }
    setSending(true);
    try {
      const created = await surveysService.startPublicResponse({
        publicId: survey.publicId, score, comment: comment.trim(), page, inviteId,
      });
      setResponse(created);
    } catch(err) {
      toast.error(getTranslation(languageCode, 'sendFailed'));
    }
    setSending(false);
  };

  if(response && !response.isComplete) {
    return <SurveyChat survey={survey} initialResponse={response} />;
  }

  if(response) {
    return (
      <div
        className="rounded-xl border border-gray-100 p-6 text-center shadow-sm"
        style={{ backgroundColor: design.thankYouBackgroundColor || '#ffffff', color: readableOn(design.thankYouBackgroundColor || '#ffffff') }}
      >
        <div
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
          style={{ backgroundColor: design.thankYouIconColor || '#6366f1', color: readableOn(design.thankYouIconColor || '#6366f1') }}
        >
          <ThumbsUp className="h-6 w-6" />
        </div>
        <h4 className="mb-2 text-lg font-semibold">{getTranslation(languageCode, 'thankYouTitle')}</h4>
        <p className="text-sm opacity-80">{getTranslation(languageCode, 'thankYouMessage')}</p>
        {preview && (
          <button type="button" onClick={() => setResponse(null)} className="mt-4 text-xs underline opacity-70">
            {getTranslation(languageCode, 'tryAgain')}
          </button>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl p-6 shadow-xl"
      style={{ backgroundColor: design.chatBackgroundColor || '#f8fafc', color: design.textColor || '#374151' }}
    >
      <p className="mb-4 text-center text-lg font-semibold" style={{ color: design.titleColor || '#1f2937' }}>
        {fixed.question}
      </p>

      {fixed.scale === 'nps' && (
        <div>
          <div className="grid grid-cols-11 gap-1">
            {Array.from({ length: 11 }, (_, n) => (
              <ScoreButton key={n} chosen={score === n} onClick={() => setScore(n)} color={buttonColor}>
                {n}
              </ScoreButton>
            ))}
          </div>
          <div className="mt-1 flex justify-between text-xs opacity-60">
            <span>{getTranslation(languageCode, 'notLikely')}</span>
            <span>{getTranslation(languageCode, 'veryLikely')}</span>
          </div>
        </div>
      )}

      {fixed.scale === 'emoji' && (
        <div className="flex justify-center gap-2">
          {EMOJIS.map((emoji, index) => (
            <ScoreButton
              key={emoji}
              chosen={score === index + 1}
              onClick={() => setScore(index + 1)}
              color={buttonColor}
              large
            >
              <span role="img" aria-label={`${index + 1} of 5`}>{emoji}</span>
            </ScoreButton>
          ))}
        </div>
      )}

      {fixed.scale === 'stars' && (
        <div className="flex justify-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setScore(n)}
              aria-label={`${n} of 5`}
              className="p-1 transition hover:scale-110"
            >
              <Star size={34} className={score !== null && n <= score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
            </button>
          ))}
        </div>
      )}

      {fixed.askComment !== false && (
        <textarea
          rows={3}
          maxLength={2000}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={fixed.commentPrompt || getTranslation(languageCode, 'placeholder')}
          className="mt-5 w-full rounded-lg px-4 py-3 text-sm shadow-sm focus:outline-none"
          style={{
            backgroundColor: design.inputBackgroundColor || '#ffffff',
            color: readableOn(design.inputBackgroundColor || '#ffffff'),
            border: `1px solid ${design.inputBorderColor || '#d1d5db'}`,
          }}
        />
      )}

      <button
        type="submit"
        disabled={score === null || sending}
        className="mt-5 flex w-full items-center justify-center rounded-full py-3 font-medium shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: buttonColor, color: buttonText }}
      >
        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : getTranslation(languageCode, 'submit')}
      </button>
    </form>
  );
}

function ScoreButton({
  chosen, onClick, color, large = false, children,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={chosen}
      className={`rounded-lg border transition ${large ? 'h-14 w-14 text-3xl' : 'py-2 text-sm font-medium'} ${
        chosen ? 'scale-105 shadow' : 'bg-white text-gray-700 hover:border-gray-400'
      }`}
      style={chosen ? { backgroundColor: color, borderColor: color, color: readableOn(color) } : { borderColor: '#e5e7eb' }}
    >
      {children}
    </button>
  );
}

function readableOn(background) {
  return tinycolor.mostReadable(background, ['#111827', '#ffffff']).toHexString();
}

export default FixedSurvey;
